import { store, getContext } from '@wordpress/interactivity';
import { getCaddyNonce } from '../../core/shared/api.js';
import { convertStoreApiItemToCaddyFormat, convertCartItems } from '../../core/shared/converters.js';
import { convertStoreApiPrice } from '../../core/shared/formatters.js';

let _headerOriginalContent = null;

function updateHeaderForTab(tab) {
	const headerTitle = document.querySelector('.cc-header-title');
	const closeIcon = document.querySelector('.cc-header-back-close');
	const arrowIcon = document.querySelector('.cc-header-back-arrow');

	if (headerTitle) {
		if (!_headerOriginalContent) {
			_headerOriginalContent = headerTitle.cloneNode(true);
		}
		if (tab === 'cart') {
			headerTitle.replaceChildren(..._headerOriginalContent.cloneNode(true).childNodes);
		} else {
			const { state } = store('caddy/cart');
			const titles = {
				'saves': state?.i18n?.yourSaves || 'Your Saves',
				'offers': state?.i18n?.yourOffers || 'Your Offers',
			};
			headerTitle.textContent = titles[tab] || state?.i18n?.yourCart || 'Your Cart';
		}
	}

	if (closeIcon && arrowIcon) {
		if (tab === 'cart') {
			closeIcon.style.display = '';
			arrowIcon.style.display = 'none';
		} else {
			closeIcon.style.display = 'none';
			arrowIcon.style.display = '';
		}
	}
}

/**
 * Initialize Save for Later functionality
 * Adds SFL actions to the cart store and sets up event listeners
 *
 * @param {Object} coreFunctions - Core cart functions needed by SFL
 * @returns {void}
 */
export function initializeSaveForLater(coreFunctions) {
	const {
		updateCartTotalsFromItems,
		removeItemFromServer,
		refreshCartFromServer,
		updateCartEmptyClass,
		updateCartWidgetCount,
		updateCartTotals,
		updateAppliedCouponsDisplay,
		initializeRecommendations
	} = coreFunctions;

	const cartStore = store('caddy/cart');

	// Add SFL-specific actions to the cart store
	Object.assign(cartStore.actions, {
		/**
		 * Save item for later - move from cart to saved items
		 */
		async saveForLater() {
			const { state } = store('caddy/cart');
			const context = getContext();
			const cartKey = context.item?.cartKey;
			const productId = context.item?.productId;

			if (!cartKey || !productId) {
				return;
			}

			// Find the item in cart for optimistic update
			const itemIndex = state.items.findIndex(item => item.cartKey === cartKey);
			if (itemIndex === -1) {
				return;
			}

			const itemToSave = { ...state.items[itemIndex] };

			// Optimistic update: remove from cart
			state.items.splice(itemIndex, 1);
			state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
			state.isItemSingular = state.cartCount === 1;
			const rawSubtotal = state.items.reduce((total, item) => total + item.lineTotal, 0);
			state.cartSubtotal = Math.round(rawSubtotal * 100) / 100;
			updateCartTotalsFromItems(state, true);
			updateCartEmptyClass(state.cartCount);
			updateCartWidgetCount(state.cartCount);

			try {
				const response = await fetch('/wp-json/caddy/v1/saved-items/add', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-WP-Nonce': getCaddyNonce()
					},
					body: JSON.stringify({
						cart_key: cartKey,
						product_id: productId
					})
				});

				const data = await response.json();

				if (!response.ok || !data.success) {
					throw new Error(data.message || state.i18n.errorSaveItemFailed);
				}

				// Update saved items
				state.savedItems = data.saved_items || [];

			} catch (error) {
				// Rollback: add item back to cart
				state.items.splice(itemIndex, 0, itemToSave);
				state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
				state.isItemSingular = state.cartCount === 1;
				const rawSubtotal = state.items.reduce((total, item) => total + item.lineTotal, 0);
				state.cartSubtotal = Math.round(rawSubtotal * 100) / 100;
				updateCartTotalsFromItems(state, true);
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);
			}
		},

		/**
		 * Remove item from saved items
		 */
		async removeSavedItem() {
			const { state } = store('caddy/cart');
			const context = getContext();
			const productId = context.item?.productId;

			if (!productId) {
				return;
			}

			// Store current state for rollback
			const originalSavedItems = [...state.savedItems];

			// Optimistic update: remove from saved items
			state.savedItems = state.savedItems.filter(item => item.productId !== productId);
			updateSavedItemsEmptyClass(state.savedItems.length);

			try {
				const response = await fetch('/wp-json/caddy/v1/saved-items/remove', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-WP-Nonce': getCaddyNonce()
					},
					body: JSON.stringify({
						product_id: productId
					})
				});

				const data = await response.json();

				if (!response.ok || !data.success) {
					throw new Error(data.message || state.i18n.errorRemoveSavedItemFailed);
				}

				// Update with fresh data from server
				state.savedItems = data.saved_items || [];
				updateSavedItemsEmptyClass(state.savedItems.length);

			} catch (error) {
				// Rollback
				state.savedItems = originalSavedItems;
				updateSavedItemsEmptyClass(state.savedItems.length);
			}
		},

		/**
		 * Move saved item to cart
		 */
		async moveToCart() {
			const { state } = store('caddy/cart');
			const context = getContext();
			const productId = context.item?.productId;

			if (!productId) {
				return;
			}

			// Show saving/loading spinner on the item
			const itemIndex = state.savedItems.findIndex(item => item.productId === productId);
			if (itemIndex !== -1) {
				state.savedItems[itemIndex].isMoving = true;
			}

			// Set flag to prevent wc_add_to_cart listener from firing
			window._caddyMovingToCart = true;

			// Store current state for rollback
			const originalSavedItems = [...state.savedItems];

			try {
				// Step 1: Add to cart using Store API
				const addToCartData = {
					id: parseInt(productId),
					quantity: 1
				};

				const storeApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart/add-item`;

				// Get Store API nonce
				let storeApiNonce = null;
				const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
				if (storeApiNonceMeta) {
					storeApiNonce = storeApiNonceMeta.getAttribute('content');
				}

				const headers = {
					'Content-Type': 'application/json',
				};
				if (storeApiNonce) {
					headers['Nonce'] = storeApiNonce;
				}

				const storeApiResponse = await fetch(storeApiUrl, {
					method: 'POST',
					credentials: 'same-origin',
					headers: headers,
					body: JSON.stringify(addToCartData)
				});

				if (!storeApiResponse.ok) {
					throw new Error(state.i18n.errorAddToCartFailed);
				}

				// Store API add-item returns full cart - use it directly!
				const cartData = await storeApiResponse.json();

				// Convert Store API format to Caddy format
				const caddyItems = convertCartItems(cartData.items);
				state.items = caddyItems;
				state.cartCount = cartData.items_count;
				state.isItemSingular = cartData.items_count === 1;
				updateCartTotals(state, cartData);
				state.coupons = cartData.coupons || [];
				state.discountTotal = cartData.totals.total_discount ? convertStoreApiPrice(cartData.totals.total_discount) : 0;
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);

				// Update applied coupons display to refresh discount amount
				updateAppliedCouponsDisplay(cartData.coupons || []);

				// Step 2: Remove from saved items
				const removeResponse = await fetch('/wp-json/caddy/v1/saved-items/remove', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-WP-Nonce': getCaddyNonce()
					},
					body: JSON.stringify({
						product_id: productId
					})
				});

				const removeData = await removeResponse.json();
				if (removeResponse.ok && removeData.success) {
					state.savedItems = removeData.saved_items || state.savedItems;
					updateSavedItemsEmptyClass(state.savedItems.length);
				}

				// Update recommendations with new cart items
				initializeRecommendations();

			} catch (error) {
				// Clear the moving spinner
				const failedIndex = state.savedItems.findIndex(item => item.productId === productId);
				if (failedIndex !== -1) {
					state.savedItems[failedIndex].isMoving = false;
				}
			} finally {
				// Reset flag
				window._caddyMovingToCart = false;
			}
		}
	});

	// Setup SFL UI and event handlers
	setupTabSystem();
	setupSaveForLaterEvents(coreFunctions);
	setupWidgetEvents();
}

/**
 * Update the saved items empty class based on saved items count
 */
function updateSavedItemsEmptyClass(savedItemsCount) {
	const savedBody = document.querySelector('.cc-saves .cc-body');
	const emptyState = document.getElementById('cc-empty-saved-items');

	if (!savedBody) return;

	if (savedItemsCount === 0) {
		savedBody.classList.add('cc-empty');
		if (emptyState) {
			emptyState.classList.remove('cc-hidden');
		}
	} else {
		savedBody.classList.remove('cc-empty');
		if (emptyState) {
			emptyState.classList.add('cc-hidden');
		}
	}
}

/**
 * Update saved items widget count
 */
function updateSavedItemsWidgetCount(count) {
	const savedWidgets = document.querySelectorAll('.cc_saved_items_list');
	savedWidgets.forEach(widget => {
		let countSpan = widget.querySelector('.cc_saved_count');

		if (count > 0) {
			if (!countSpan) {
				countSpan = document.createElement('span');
				countSpan.className = 'cc_saved_count';
				widget.appendChild(document.createTextNode(' '));
				widget.appendChild(countSpan);
			}
			countSpan.textContent = `(${count})`;
		} else {
			if (countSpan) {
				const textNode = countSpan.previousSibling;
				if (textNode && textNode.nodeType === Node.TEXT_NODE) {
					textNode.remove();
				}
				countSpan.remove();
			}
		}
	});
}

/**
 * Setup tab system for Cart / Saved Items navigation
 */
function setupTabSystem() {
	// Set initial aria-selected state on cart tab (default tab)
	const cartNavLink = document.querySelector('.cc-cart-nav');
	const saveNavLink = document.querySelector('.cc-save-nav');

	if (cartNavLink) {
		cartNavLink.setAttribute('aria-selected', 'true');
		cartNavLink.classList.add('active');
	}

	if (saveNavLink) {
		saveNavLink.setAttribute('aria-selected', 'false');
		saveNavLink.classList.remove('active');
	}

	// Handle tab navigation clicks
	document.addEventListener('click', function(e) {
		// Handle "View Saved Items" button clicks
		if (e.target.matches('.cc-view-saved-items')) {
			e.preventDefault();
			switchToSavedTab();
		}

		// Handle "Saved Items" tab link clicks
		const savedNavLink = e.target.closest('.cc-save-nav');
		if (savedNavLink) {
			e.preventDefault();
			switchToSavedTab();
		}

		// Handle "Your Cart" tab link clicks
		const cartNavLink = e.target.closest('.cc-cart-nav');
		if (cartNavLink) {
			e.preventDefault();
			switchToCartTab();
		}

		// Handle back to cart navigation
		if (e.target.matches('.cc-back-to-cart, .cc-tab-cart')) {
			e.preventDefault();
			switchToCartTab();
		}
	});

	async function switchToSavedTab() {
		const cartNavLink = document.querySelector('.cc-cart-nav');
		const saveNavLink = document.querySelector('.cc-save-nav');

		if (cartNavLink) {
			cartNavLink.setAttribute('aria-selected', 'false');
			cartNavLink.classList.remove('active');
		}

		if (saveNavLink) {
			saveNavLink.setAttribute('aria-selected', 'true');
			saveNavLink.classList.add('active');
		}

		// Hide cart tab content
		const cartTab = document.querySelector('.cc-cart');
		if (cartTab) {
			cartTab.classList.add('cc-tab-inactive');
		}

		// Show saved list tab content
		const savedTab = document.querySelector('#cc-saves');
		if (savedTab) {
			savedTab.classList.add('cc-tab-active');
		}

		// Update header title and back icon
		updateHeaderForTab('saves');

		// Load saved items when tab is opened
		try {
			const response = await fetch('/wp-json/caddy/v1/saved-items', {
				credentials: 'same-origin',
				headers: {
					'X-WP-Nonce': getCaddyNonce()
				}
			});

			if (response.ok) {
				const data = await response.json();
				if (data.success) {
					const cartStore = store('caddy/cart');
					cartStore.state.savedItems = data.saved_items || [];
					updateSavedItemsEmptyClass(cartStore.state.savedItems.length);
					updateSavedItemsWidgetCount(cartStore.state.savedItems.length);

					const emptyState = document.getElementById('cc-empty-saved-items');
					if (emptyState) {
						if (cartStore.state.savedItems.length === 0) {
							emptyState.classList.remove('cc-hidden');
						} else {
							emptyState.classList.add('cc-hidden');
						}
					}
				}
			}
		} catch (error) {
			// Failed to load saved items
		}
	}

	function switchToCartTab() {
		const cartNavLink = document.querySelector('.cc-cart-nav');
		const saveNavLink = document.querySelector('.cc-save-nav');

		if (saveNavLink) {
			saveNavLink.setAttribute('aria-selected', 'false');
			saveNavLink.classList.remove('active');
		}

		if (cartNavLink) {
			cartNavLink.setAttribute('aria-selected', 'true');
			cartNavLink.classList.add('active');
		}

		// Hide saved list tab content
		const savedTab = document.querySelector('#cc-saves');
		if (savedTab) {
			savedTab.classList.remove('cc-tab-active');
		}

		// Show cart tab content
		const cartTab = document.querySelector('.cc-cart');
		if (cartTab) {
			cartTab.classList.remove('cc-tab-inactive');
		}

		// Restore header title and close icon
		updateHeaderForTab('cart');
	}

	// Initialize default tab state
	const cartTab = document.querySelector('.cc-cart');
	const savedTab = document.querySelector('#cc-saves');

	if (cartTab) {
		cartTab.classList.remove('cc-tab-inactive');
	}

	if (savedTab) {
		savedTab.classList.remove('cc-tab-active');
	}

	// Export global tab switching functions for other modules
	window._caddySwitchToSavedTab = switchToSavedTab;
	window._caddySwitchToCartTab = switchToCartTab;
}

/**
 * Setup Save for Later event listeners
 */
function setupSaveForLaterEvents(coreFunctions) {
	const { updateCartTotalsFromItems, removeItemFromServer, updateCartEmptyClass, updateCartWidgetCount } = coreFunctions;

	// Handle Save for Later button clicks
	document.addEventListener('click', async function(e) {
		// Handle cart Save for Later buttons (move from cart to saved)
		if (e.target.matches('.save_for_later_btn, .save-for-later-btn')) {
			e.preventDefault();

			const cartKey = e.target.dataset.cart_item_key || e.target.dataset.cartKey;
			const productId = parseInt(e.target.dataset.product_id);

			if (!cartKey || !productId) {
				return;
			}

			// Call save for later function
			await saveItemForLater(cartKey, productId, { updateCartTotalsFromItems, removeItemFromServer, updateCartEmptyClass, updateCartWidgetCount });
		}

		// Handle product page Save for Later buttons (add directly to saved)
		const addSflButton = e.target.closest('.cc_add_product_to_sfl');
		if (addSflButton) {
			e.preventDefault();

			const productId = parseInt(addSflButton.dataset.product_id);

			if (!productId) {
				return;
			}

			await addProductToSavedItems(productId);
		}

		// Handle Remove from Saved Items button clicks (product pages)
		const removeSflButton = e.target.closest('.remove_from_sfl_button');
		if (removeSflButton) {
			e.preventDefault();

			const productId = parseInt(removeSflButton.dataset.product_id);

			if (!productId) {
				return;
			}

			await removeProductFromSavedItems(productId);
		}
	});
}

/**
 * Setup widget event listeners
 */
function setupWidgetEvents() {
	document.addEventListener('click', function(e) {
		// Handle cart widget clicks
		const cartWidget = e.target.closest('.cc_cart_items_list');
		if (cartWidget) {
			e.preventDefault();

			const cartStore = store('caddy/cart');
			cartStore.actions.openCart();

			setTimeout(() => {
				if (window._caddySwitchToCartTab) {
					window._caddySwitchToCartTab();
				}
			}, 50);
		}

		// Handle saved items widget clicks
		const savedWidget = e.target.closest('.cc_saved_items_list');
		if (savedWidget) {
			e.preventDefault();

			// Open cart and switch directly to saves tab
			const cartStore = store('caddy/cart');
			cartStore.actions.openCart('saves');
		}
	});
}

/**
 * Save item for later (legacy function for direct button clicks)
 */
async function saveItemForLater(cartKey, productId, coreFunctions) {
	const { updateCartTotalsFromItems, removeItemFromServer, updateCartEmptyClass, updateCartWidgetCount } = coreFunctions;
	const cartStore = store('caddy/cart');

	// Find the item in cart
	const itemIndex = cartStore.state.items.findIndex(item => item.cartKey === cartKey);
	if (itemIndex === -1) {
		return;
	}

	const removedItem = cartStore.state.items[itemIndex];

	// Show saving spinner on the item
	cartStore.state.items[itemIndex].isSaving = true;

	try {
		// Step 1: Save to saved items list
		const saveResponse = await fetch('/wp-json/caddy/v1/saved-items/add', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': getCaddyNonce()
			},
			body: JSON.stringify({
				product_id: productId
			})
		});

		const saveData = await saveResponse.json();

		if (!saveResponse.ok || !saveData.success) {
			const { state } = store('caddy/cart');
			throw new Error(saveData.message || state.i18n.errorSaveItemFailed);
		}

		// Step 2: Remove from cart display (after API success)
		const currentIndex = cartStore.state.items.findIndex(item => item.cartKey === cartKey);
		if (currentIndex !== -1) {
			cartStore.state.items.splice(currentIndex, 1);
		}
		cartStore.state.cartCount = cartStore.state.items.reduce((total, item) => total + item.quantity, 0);
		cartStore.state.isItemSingular = cartStore.state.cartCount === 1;
		updateCartTotalsFromItems(cartStore.state);
		updateCartEmptyClass(cartStore.state.cartCount);
		updateCartWidgetCount(cartStore.state.cartCount);

		// Step 3: Remove from WooCommerce cart via Store API
		await removeItemFromServer(cartKey);

		// Step 4: Update saved items in the store
		cartStore.state.savedItems = saveData.saved_items || [];

	} catch (error) {
		// Clear saving state on failure
		const failedIndex = cartStore.state.items.findIndex(item => item.cartKey === cartKey);
		if (failedIndex !== -1) {
			cartStore.state.items[failedIndex].isSaving = false;
		} else if (removedItem) {
			removedItem.isSaving = false;
			cartStore.state.items.splice(itemIndex, 0, removedItem);
			cartStore.state.cartCount = cartStore.state.items.reduce((total, item) => total + item.quantity, 0);
			cartStore.state.isItemSingular = cartStore.state.cartCount === 1;
			updateCartTotalsFromItems(cartStore.state);
			updateCartEmptyClass(cartStore.state.cartCount);
			updateCartWidgetCount(cartStore.state.cartCount);
		}

		const { state } = store('caddy/cart');
		alert(state.i18n.alertSaveForLaterFailed);
	}
}

/**
 * Add product directly to saved items (from product page)
 */
async function addProductToSavedItems(productId) {
	try {
		const response = await fetch('/wp-json/caddy/v1/saved-items/add', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': getCaddyNonce()
			},
			body: JSON.stringify({
				product_id: productId,
				cart_key: ''
			})
		});

		const data = await response.json();

		if (!response.ok || !data.success) {
			const { state } = store('caddy/cart');
			throw new Error(data.message || state.i18n.errorSaveItemFailed);
		}

		// Update saved items in store
		const cartStore = store('caddy/cart');
		cartStore.state.savedItems = data.saved_items || [];
		updateSavedItemsEmptyClass(cartStore.state.savedItems.length);
		updateSavedItemsWidgetCount(cartStore.state.savedItems.length);

		// Update button state to show "Saved"
		const button = document.querySelector(`[data-product_id="${productId}"].cc_add_product_to_sfl`);
		if (button) {
			button.classList.remove('cc_add_product_to_sfl');
			button.classList.add('remove_from_sfl_button');
			const span = button.querySelector('span');
			if (span) {
				const { state } = store('caddy/cart');
				span.textContent = state.i18n.saved;
			}
			const icon = button.querySelector('i');
			if (icon) {
				icon.classList.remove('ccicon-heart-empty');
				icon.classList.add('ccicon-heart-filled');
			}
		}

	} catch (error) {
		// Failed to add product to saved items
	}
}

/**
 * Remove product from saved items (from product page)
 */
async function removeProductFromSavedItems(productId) {
	try {
		const response = await fetch('/wp-json/caddy/v1/saved-items/remove', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': getCaddyNonce()
			},
			body: JSON.stringify({
				product_id: productId
			})
		});

		const data = await response.json();

		if (!response.ok || !data.success) {
			const { state } = store('caddy/cart');
			throw new Error(data.message || state.i18n.errorRemoveSavedItemFailed);
		}

		// Update saved items in store
		const cartStore = store('caddy/cart');
		cartStore.state.savedItems = data.saved_items || [];
		updateSavedItemsEmptyClass(cartStore.state.savedItems.length);
		updateSavedItemsWidgetCount(cartStore.state.savedItems.length);

		// Update button state back to "Save for later"
		const button = document.querySelector(`[data-product_id="${productId}"].remove_from_sfl_button`);
		if (button) {
			button.classList.remove('remove_from_sfl_button');
			button.classList.add('cc_add_product_to_sfl');
			const span = button.querySelector('span');
			if (span) {
				const { state } = store('caddy/cart');
				span.textContent = state.i18n.saveForLater;
			}
			const icon = button.querySelector('i');
			if (icon) {
				icon.classList.remove('ccicon-heart-filled');
				icon.classList.add('ccicon-heart-empty');
			}
		}

	} catch (error) {
		// Failed to remove product from saved items
	}
}
